import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import { format } from 'date-fns';
import { ACTIVITIES_DATA } from '@amplify/instruction-groups';
import Spinner from '../../../components/spinner/Spinner';
import DropDown from '../../../components/dropdown/DropDown';
import { getUrlParams } from '../../../utils/shared/urlUtils';
import getInstructionTabs from '../../navigation/navTabs';
import NavigationBar from '../../navigation/NavigationBar';
import {
    getGradeTitle,
    DROPDOWN_ITEMS_LENGTH_EQUAL_ONE,
    SUPPORTED_GRADES,
} from '../../../constants/shared/GradesConstants';
import AllActivities from './allActivities/AllActivities';
import {
    DATE_FORMAT,
    INSTRUCTION,
    LICENSE,
    PERIOD,
} from '../../../constants/shared/CommonConstants';
import { SWITCH_TO_PREVIOUS_RECOMMENDATIONS_DELAY } from '../../../constants/shared/InstructionConstants';
import Groups from './groups/Groups';
import SecureResultsBox from '../reading/SecureResultsBox';
import './Instruction.scss';
import SummerPeriodInfoBox from '../reading/SummerPeriodInfoBox';

const { GROUPS, STUDENTS, ALL_ACTIVITIES } = INSTRUCTION;
const { MOY, EOY, SUMMER } = PERIOD;

const getSelectedTab = location => getUrlParams(location).instructionTab;

const isPageLoading = (date, selectedGrade, areCheckPointsFetched) =>
    (isNil(date) && SUPPORTED_GRADES.includes(selectedGrade)) || !areCheckPointsFetched;

const Instruction = ({
    licensingInfo,
    isUpdateRecommendationButtonEnabled,
    selectedDate,
    dates,
    areCheckPointsFetched,
    isCheckPointSaving,
    grades,
    selectedGrade,
    switchInstructionTab,
    switchInstructionTabLrs,
    switchInstructionDate,
    switchInstructionGrade,
    location,
    LinkWrapper,
    staffId,
    lrsTrackingInfo,
    groups,
    observationsContent,
    activitiesSkills,
    activitiesMapper,
    activitiesMessage,
    areObservationsFetching,
    saveCheckPoint,
    url,
    hiddenTabIds,
    popoverParams,
    optionalParams,
    studentsComponent,
    measureList,
    observationActivitiesMapper,
    Widget,
    groupsMessage,
    checkPointPeriod,
    BulkActivitiesWidget,
    navigateToLatestDateStart,
    navigateToLatestDateDone,
    processCheckPoints,
    fetchObservations,
    seeActivitiesToggle,
    downloadAllActivities,
    selectObservation,
    selectPage,
    selectSkill,
    downloadActivity,
    lrsLanguageConsiderationPdf,
    switchLanguageConsiderationsTabLrs,
    viewLatestUpdateRecommendation,
    sessionId,
    lastLocation,
    isGuidedTourOn,
    isDemoModeOn,
    isSecure,
    currentPeriod,
}) => {
    useEffect(() => {
        if (isNil(selectedDate) || isSecure) {
            processCheckPoints();
        }

        if (isEmpty(observationsContent)) {
            fetchObservations();
        }
    }, []);

    useEffect(() => {
        if (
            areCheckPointsFetched &&
            isEmpty(dates) &&
            SUPPORTED_GRADES.includes(selectedGrade) &&
            !isSecure
        ) {
            saveCheckPoint();
        }
    }, [selectedGrade]);

    const [isOpenOnTab, closeBanner] = useState({
        [GROUPS]: true,
        [STUDENTS]: true,
    });

    const selectedTab = getSelectedTab(location) || GROUPS;

    const navigateToLatestDate = ({ buttonText, date }) => {
        navigateToLatestDateStart();

        // Simulate data refresh
        // Add extra delay to prevent miss clicks over button
        setTimeout(() => {
            navigateToLatestDateDone();
            viewLatestUpdateRecommendation({ buttonText, date });
        }, SWITCH_TO_PREVIOUS_RECOMMENDATIONS_DELAY);
    };

    const handlePastDateSelect = prevDate => date => {
        switchInstructionDate({ date }, prevDate);
    };

    const handleTabSwitch = tab => {
        switchInstructionTab(tab);
        switchInstructionTabLrs(tab);
    };

    const SecureResultBanner = (
        <SecureResultsBox
            selectedTab={selectedTab}
            closeBanner={closeBanner}
            isOpenOnTab={isOpenOnTab}
            isEmptyDataMessage={isSecure && isEmpty(groups) && checkPointPeriod === MOY}
        />
    );

    const SummerPeriodBanner = (
        <SummerPeriodInfoBox
            selectedTab={selectedTab}
            closeBanner={closeBanner}
            isOpenOnTab={isOpenOnTab}
            isEmptyDataMessage={
                isEmpty(groups) && (checkPointPeriod === EOY || checkPointPeriod === MOY)
            }
            currentPeriod={currentPeriod}
            selectedGrade={selectedGrade}
        />
    );

    const isLouisiana = licensingInfo.includes(LICENSE.LOUISIANA);
    const isSecureBannerShown =
        isOpenOnTab[selectedTab] && isSecure && !isDemoModeOn && currentPeriod !== SUMMER;
    const isSummerBannerShown =
        isLouisiana && isOpenOnTab[selectedTab] && !isDemoModeOn && currentPeriod === SUMMER;

    let studentsPage;

    if (isSecureBannerShown) {
        studentsPage = React.cloneElement(studentsComponent, { SecureResultBanner });
    } else if (isSummerBannerShown) {
        studentsPage = React.cloneElement(studentsComponent, { SummerPeriodBanner });
    } else {
        studentsPage = studentsComponent;
    }

    let instructionPage;
    switch (selectedTab) {
        case STUDENTS:
            instructionPage = studentsPage;
            break;
        case ALL_ACTIVITIES:
            instructionPage = (
                <AllActivities
                    LinkWrapper={LinkWrapper}
                    message={activitiesMessage}
                    skills={activitiesSkills}
                    activitiesData={ACTIVITIES_DATA}
                    groupsToActivitiesMapper={activitiesMapper}
                    staffId={staffId}
                    lrsTrackingInfo={lrsTrackingInfo}
                    selectSkill={selectSkill}
                    downloadActivity={downloadActivity}
                    isGuidedTourOn={isGuidedTourOn}
                />
            );
            break;
        default:
            instructionPage = (
                <Groups
                    groups={groups}
                    date={dates[0]}
                    isUpdateRecommendationButtonEnabled={isUpdateRecommendationButtonEnabled}
                    selectedGrade={selectedGrade}
                    isCheckPointSaving={isCheckPointSaving}
                    saveCheckPoint={saveCheckPoint}
                    navigateToLatestDate={navigateToLatestDate}
                    viewLatestUpdateRecommendation={viewLatestUpdateRecommendation}
                    observationsContent={observationsContent}
                    activitiesData={ACTIVITIES_DATA}
                    measureList={measureList}
                    observationActivitiesMapper={observationActivitiesMapper}
                    Widget={Widget}
                    groupsMessage={groupsMessage}
                    staffId={staffId}
                    lrsTrackingInfo={lrsTrackingInfo}
                    checkPointPeriod={checkPointPeriod}
                    BulkActivitiesWidget={BulkActivitiesWidget}
                    isDemoModeOn={isDemoModeOn}
                    isGuidedTourOn={isGuidedTourOn}
                    seeActivitiesToggle={seeActivitiesToggle}
                    downloadAllActivities={downloadAllActivities}
                    selectObservation={selectObservation}
                    selectPage={selectPage}
                    lrsLanguageConsiderationPdf={lrsLanguageConsiderationPdf}
                    switchLanguageConsiderationsTabLrs={switchLanguageConsiderationsTabLrs}
                    sessionId={sessionId}
                    lastLocation={lastLocation}
                    isSecure={isSecure}
                    SecureResultBanner={isSecureBannerShown && SecureResultBanner}
                    SummerPeriodBanner={isSummerBannerShown && SummerPeriodBanner}
                    currentPeriod={currentPeriod}
                    isLouisiana={isLouisiana}
                />
            );
    }
    if (isPageLoading(selectedDate, selectedGrade, areCheckPointsFetched)) {
        return <Spinner />;
    }

    return (
        <div className="container-wrap">
            <div className="tabs-container space-between">
                <NavigationBar
                    ulTagClassName="nav nav-tabs"
                    divTagClassName="d-flex align-items-end"
                    tabs={getInstructionTabs(
                        location,
                        url,
                        optionalParams,
                        popoverParams,
                        hiddenTabIds,
                    )}
                    selectedTabId={selectedTab}
                    onNavigationTabClick={handleTabSwitch}
                    popoverClassName="nav-popover"
                    LinkWrapper={LinkWrapper}
                />
                {selectedTab !== ALL_ACTIVITIES && (
                    <div className="dropdowns-block align-items-end">
                        {!isEmpty(dates) && (
                            <DropDown
                                dropDownClassNames="nav-btn instr-dates-dropdown"
                                buttonClassNames="btn-secondary btn-small"
                                dropDownItemClassNames="btn-small"
                                label="Past Updates"
                                onDropDownItemClick={handlePastDateSelect(selectedDate)}
                                chosenItem={{
                                    uid: selectedDate,
                                    name: format(new Date(selectedDate), DATE_FORMAT.DATE_TIME),
                                }}
                                items={dates.map(date => ({
                                    uid: date,
                                    name: format(new Date(date), DATE_FORMAT.DATE_TIME),
                                }))}
                                isDisabled={dates.length === DROPDOWN_ITEMS_LENGTH_EQUAL_ONE}
                            />
                        )}
                        <DropDown
                            dropDownClassNames="nav-btn"
                            buttonClassNames="btn-secondary btn-small"
                            dropDownItemClassNames="btn-small"
                            onDropDownItemClick={switchInstructionGrade}
                            chosenItem={{
                                uid: selectedGrade,
                                name: getGradeTitle(selectedGrade),
                            }}
                            items={grades.map(grade => ({
                                uid: grade,
                                name: getGradeTitle(grade),
                            }))}
                            isDisabled={grades.length === DROPDOWN_ITEMS_LENGTH_EQUAL_ONE}
                        />
                    </div>
                )}
            </div>
            <div className="content-container">
                {isCheckPointSaving || areObservationsFetching ? <Spinner /> : instructionPage}
            </div>
        </div>
    );
};

Instruction.defaultProps = {
    licensingInfo: [],
    checkPointPeriod: '',
    isUpdateRecommendationButtonEnabled: false,
    selectedDate: null,
    dates: [],
    grades: [],
    groups: [],
    selectedGrade: '',
    observationsContent: [],
    activitiesSkills: [],
    activitiesMapper: {},
    activitiesMessage: null,
    isCheckPointSaving: false,
    areCheckPointsFetched: false,
    areObservationsFetching: false,
    url: '',
    hiddenTabIds: [],
    popoverParams: {},
    optionalParams: {},
    studentsComponent: () => <></>,
    fetchObservations: () => {},
    switchInstructionDate: () => {},
    switchInstructionGrade: () => {},
    switchInstructionTab: () => {},
    processCheckPoints: () => {},
    saveCheckPoint: () => {},
    measureList: [],
    observationActivitiesMapper: {},
    Widget: () => <></>,
    BulkActivitiesWidget: () => <></>,
    groupsMessage: null,
    staffId: null,
    lrsTrackingInfo: {},
    navigateToLatestDateStart: () => {},
    navigateToLatestDateDone: () => {},
    seeActivitiesToggle: () => {},
    downloadAllActivities: () => {},
    selectObservation: () => {},
    selectPage: () => {},
    selectSkill: () => {},
    downloadActivity: () => {},
    switchInstructionTabLrs: () => {},
    lrsLanguageConsiderationPdf: () => {},
    switchLanguageConsiderationsTabLrs: () => {},
    viewLatestUpdateRecommendation: () => {},
    sessionId: '',
    lastLocation: {},
    isGuidedTourOn: false,
    isDemoModeOn: false,
    isSecure: false,
    currentPeriod: '',
};

Instruction.propTypes = {
    licensingInfo: PropTypes.array,
    checkPointPeriod: PropTypes.string,
    isUpdateRecommendationButtonEnabled: PropTypes.bool,
    selectedDate: PropTypes.string,
    dates: PropTypes.array,
    grades: PropTypes.array,
    groups: PropTypes.array,
    selectedGrade: PropTypes.string,
    observationsContent: PropTypes.array,
    activitiesSkills: PropTypes.array,
    activitiesMapper: PropTypes.object,
    activitiesMessage: PropTypes.string,
    isCheckPointSaving: PropTypes.bool,
    areCheckPointsFetched: PropTypes.bool,
    areObservationsFetching: PropTypes.bool,
    url: PropTypes.string,
    hiddenTabIds: PropTypes.array,
    popoverParams: PropTypes.object,
    optionalParams: PropTypes.object,
    studentsComponent: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    fetchObservations: PropTypes.func,
    LinkWrapper: PropTypes.func.isRequired,
    switchInstructionDate: PropTypes.func,
    switchInstructionGrade: PropTypes.func,
    switchInstructionTab: PropTypes.func,
    processCheckPoints: PropTypes.func,
    saveCheckPoint: PropTypes.func,
    measureList: PropTypes.array,
    observationActivitiesMapper: PropTypes.object,
    Widget: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    BulkActivitiesWidget: PropTypes.func,
    groupsMessage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    staffId: PropTypes.string,
    lrsTrackingInfo: PropTypes.object,
    location: PropTypes.object.isRequired,
    navigateToLatestDateStart: PropTypes.func,
    navigateToLatestDateDone: PropTypes.func,
    seeActivitiesToggle: PropTypes.func,
    downloadAllActivities: PropTypes.func,
    selectObservation: PropTypes.func,
    selectPage: PropTypes.func,
    selectSkill: PropTypes.func,
    downloadActivity: PropTypes.func,
    switchInstructionTabLrs: PropTypes.func,
    lrsLanguageConsiderationPdf: PropTypes.func,
    switchLanguageConsiderationsTabLrs: PropTypes.func,
    viewLatestUpdateRecommendation: PropTypes.func,
    sessionId: PropTypes.string,
    lastLocation: PropTypes.object,
    isGuidedTourOn: PropTypes.bool,
    isDemoModeOn: PropTypes.bool,
    isSecure: PropTypes.bool,
    currentPeriod: PropTypes.string,
};

export default Instruction;
